import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { boundaryIsSafe, browser, reloadPage, reloadUnlessBusy } from "./deferredNavigation";
import { practiceAppName } from "../config/practice";

// Keeps long-open tabs current without anyone hard-refreshing.
//
// index.html is served no-cache and the JS bundle is content-hashed, so a full
// page load always fetches the newest build — but an already-open SPA keeps
// running the bundle it booted with until something triggers a full load. This
// hook polls asset-manifest.json (which CRA regenerates each build with the new
// main.<hash>.js), and when the hash differs from the one this tab started on,
// it reloads at a SAFE moment: the next route change, or when the user returns
// to a tab they'd left (visibilitychange; a window focus only re-checks, since
// it lands between the mousedown and the click of whatever was clicked). It
// never reloads while a recording, its note or a voice recording is in
// progress, while the chart is open, or while someone is typing or in a dialog
// (boundaryIsSafe, utils/deferredNavigation). A small banner also offers a
// manual reload -- during a recording that one is queued instead.
// PUBLIC_URL-relative: a branch preview served under /branch/<name> must read its
// own manifest, not the root deploy's, or it sees a permanent hash mismatch and
// reloads forever. Empty in prod, so the path is unchanged there.
const MANIFEST = `${(process.env.PUBLIC_URL || "").replace(/\/$/, "")}/asset-manifest.json`;
const POLL_MS = 3 * 60 * 1000;

// The MedTrack wall board (/medtrack/<ak>) runs on an unattended TV that never
// changes page, so the banner would sit over the board until someone walked
// up to it (OSA gap 14). There a new build loads by itself instead -- the
// board has no recording, typing or dialogs to protect. In fullscreen opened
// with the board's own button (Fullscreen API) a reload would drop the board
// out of fullscreen, so it waits until fullscreen ends; browser/kiosk
// fullscreen (F11, --kiosk) survives a reload and is not affected.
export const isUnattendedBoard = (pathname = window.location.pathname) =>
  /\/medtrack\/[^/]+/.test(pathname || "");

let boardReloadArmed = false;
export function reloadBoardWhenAllowed(doc = document, reload = () => browser.reload()) {
  if (boardReloadArmed) return;
  boardReloadArmed = true;
  if (!doc.fullscreenElement) {
    reload();
    return;
  }
  const onChange = () => {
    if (doc.fullscreenElement) return;
    doc.removeEventListener("fullscreenchange", onChange);
    reload();
  };
  doc.addEventListener("fullscreenchange", onChange);
}
// Tests only.
export function resetBoardReloadForTests() {
  boardReloadArmed = false;
}

async function fetchMainHash() {
  try {
    const res = await fetch(`${MANIFEST}?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return (data.files && data.files["main.js"]) || null;
  } catch {
    return null;
  }
}

export default function useAutoUpdate() {
  const location = useLocation();
  const bootHash = useRef(null);
  const pending = useRef(false);
  const [updateReady, setUpdateReady] = useState(false);

  // Record the hash this tab is running, and poll for a newer one.
  useEffect(() => {
    let alive = true;
    let timer = null;

    const check = async () => {
      const h = await fetchMainHash();
      if (!alive || !h) return;
      if (!bootHash.current) {
        bootHash.current = h;
        return;
      }
      if (h !== bootHash.current) {
        if (isUnattendedBoard()) {
          reloadBoardWhenAllowed();
          return;
        }
        pending.current = true;
        setUpdateReady(true);
      }
    };

    check();
    timer = setInterval(check, POLL_MS);
    // In Node/jsdom (the build smoke test) setInterval returns a Timeout with
    // .unref(), which lets the process exit instead of being held open by this
    // poller. In browsers setInterval returns a number, so this is a no-op.
    if (timer && typeof timer.unref === "function") timer.unref();
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // Returning to a tab that's been left is a safe moment to swap builds.
      if (pending.current && boundaryIsSafe()) {
        window.location.reload();
      } else {
        check();
      }
    };
    const onFocus = () => {
      check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // A deliberate navigation is a safe boundary to reload on.
  useEffect(() => {
    if (pending.current && boundaryIsSafe()) {
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return {
    updateReady,
    // A click: reloads now unless a recording, its note or a voice recording
    // is in progress; then it is queued (notice with Refresh once it is done).
    reloadNow: () => {
      if (reloadUnlessBusy()) return;
      reloadPage({
        reason: "update",
        subject: `A new version of ${practiceAppName()} is ready`,
        verb: "Refresh to load it",
      });
    },
  };
}
